import { memo } from 'react';
import { Users, TrendingUp, Phone, Linkedin, Instagram, Video } from 'lucide-react';

export const SocialMedia = memo(({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-semibold">Error loading social media data</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        No data available
      </div>
    );
  }

  const { platforms, summary } = data;

  const platformIcons = {
    linkedin: Linkedin,
    instagramPersonal: Instagram,
    instagramBusiness: Instagram,
    tiktok: Video,
    youtube: Video,
  };

  const platformColors = {
    linkedin: 'bg-blue-50 text-blue-700',
    instagramPersonal: 'bg-pink-50 text-pink-700',
    instagramBusiness: 'bg-purple-50 text-purple-700',
    tiktok: 'bg-gray-50 text-gray-700',
    youtube: 'bg-red-50 text-red-700',
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Followers</p>
              <p className="text-3xl font-bold text-blue-700 mt-2">
                {summary.totalFollowers.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Traffic to Site</p>
              <p className="text-3xl font-bold text-green-700 mt-2">
                {summary.totalTraffic.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Booked Calls</p>
              <p className="text-3xl font-bold text-purple-700 mt-2">
                {summary.totalBookedCalls}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Phone className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Platform Metrics Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                  Platform
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Followers
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Traffic to Site
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                  Booked Calls
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(platforms).map(([key, platform]) => {
                const Icon = platformIcons[key];
                const colorClass = platformColors[key];
                
                return (
                  <tr key={key} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <p className="text-sm font-medium text-gray-900">{platform.name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center">
                        <p className="text-sm font-semibold text-gray-900">
                          {platform.followers.toLocaleString()}
                        </p>
                        <span className={`text-xs font-medium ${
                          platform.followersChange > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {platform.followersChange > 0 ? '↑' : '↓'} {Math.abs(platform.followersChange).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center">
                        <p className="text-sm font-semibold text-gray-900">
                          {platform.trafficToSite.toLocaleString()}
                        </p>
                        <span className={`text-xs font-medium ${
                          platform.trafficChange > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {platform.trafficChange > 0 ? '↑' : '↓'} {Math.abs(platform.trafficChange).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center">
                        <p className="text-sm font-semibold text-gray-900">
                          {platform.bookedCalls}
                        </p>
                        <span className={`text-xs font-medium ${
                          platform.callsChange > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {platform.callsChange > 0 ? '↑' : '↓'} {Math.abs(platform.callsChange).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
              <tr>
                <td className="px-6 py-4 text-sm font-bold text-gray-900">
                  Total
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {summary.totalFollowers.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {summary.totalTraffic.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">
                  {summary.totalBookedCalls}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
});

SocialMedia.displayName = 'SocialMedia';